<template>
    <div
        v-auto-animate
        class="group"
        :style="{
            scrollMarginTop: `${scrollOffset}px`,
        }"
    >
        <button
            type="button"
            :aria-expanded="localIsOpen"
            :aria-controls="contentId"
            @click="toggleAccordion"
            :class="[
                'flex items-center gap-2.5 border bg-white w-full text-left',
                !hasError && 'hover:border-black',
                'focus:outline-none focus:ring-1 focus:ring-black focus:border-black',
                hasError ? 'border-red' : localIsOpen ? 'border-black border-b-0' : 'border-gray-light',
            ]"
        >
            <div
                class="h-[66px] w-[100px] object-cover bg-gray-F6F6F6 border-none shrink-0"
                :style="{
                    backgroundImage: `url(&quot;${image}&quot;)`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }"
            />
            <div class="flex items-center justify-between w-full group-hover:text-black">
                <div class="flex gap-2.5 font-semibold">
                    {{ displayName }}
                </div>
                <minus-icon
                    v-if="!selectedColor && localIsOpen"
                    class="w-[36px] h-[36px] mr-4 shrink-0 border border-black rounded-full p-2 text-black"
                    aria-label="Collapse section"
                />
                <color-swatch
                    v-else-if="selectedColor"
                    class="mr-4"
                    :color="selectedColor"
                    :translucent="isSelectedColorTranslucent"
                />
                <plus-icon
                    v-else
                    :class="[
                        'w-[36px] h-[36px] mr-4 shrink-0 border rounded-full p-2',
                        'border-[#CCCCCC] text-[#CCCCCC]',
                        'group-hover:border-black group-hover:text-black',
                    ]"
                    aria-label="Expand section"
                />
            </div>
        </button>

        <div
            v-if="localIsOpen && hasContent"
            :id="contentId"
            class="border-t border-black px-4 py-4"
        >
            <slot />
        </div>
    </div>
</template>

<script>
    import getImage from '../helpers/getimage';
    import getPlacementLabel from '../helpers/placementlabel';
    import ColorSwatch from './ColorSwatch.vue';
    import MinusIcon from './MinusIcon.vue';
    import PlusIcon from './PlusIcon.vue';

    import allStores from '../stores/all';

    export default {
        mixins: [allStores],

        components: {
            ColorSwatch,
            MinusIcon,
            PlusIcon,
        },

        emits: ['toggle', 'update:modelValue'],

        props: {
            name: {
                type: String,
                required: true,
            },

            selectedColor: {
                type: String,
                default: null,
            },

            modelValue: {
                type: Boolean,
                default: false,
            },

            scrollDelay: {
                type: Number,
                default: 500,
            },

            scrollOffset: {
                type: Number,
                default: 0,
            },

            hasError: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                localIsOpen: this.modelValue,
            };
        },

        computed: {
            isSelectedColorTranslucent() {
                if (!this.selectedColor) {
                    return false;
                }

                const selectedColor = this.selectedColor.toLowerCase();
                const paletteOption = this.customizerStore.masterPalette?.find((option) => (
                    option.color?.toLowerCase() === selectedColor
                    || option.value?.toLowerCase() === selectedColor
                ));

                return !!paletteOption?.translucent;
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            hasContent() {
                return !!this.$slots.default;
            },

            contentId() {
                const slug = this.name
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-+|-+$/g, '');

                return `step-sub-option-${slug}`;
            },

            displayName() {
                return getPlacementLabel(
                    this.viewController,
                    this.name,
                    this.$i18n,
                    this.name,
                );
            },

            image() {
                const placement = this.viewController.findPlacement(this.name);

                return getImage(this.viewController, placement);
            },
        },

        watch: {
            modelValue(value) {
                this.localIsOpen = value;

                if (value) {
                    this.scrollIntoView();
                }
            },
        },

        methods: {
            scrollIntoView() {
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.$el?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                            inline: 'nearest',
                        });
                    }, this.scrollDelay);
                });
            },

            toggleAccordion() {
                this.localIsOpen = !this.localIsOpen;
                this.$emit('update:modelValue', this.localIsOpen);
                this.$emit('toggle', this.localIsOpen);
            },
        },
    };
</script>
